import React, { useMemo } from 'react';
import useAuth from 'src/providers/hooks/useAuth';
import dynamic from 'next/dynamic';
import { Idea, Source, UsersModalType } from 'types';
import useActivity from 'src/providers/hooks/useActivity';
import useNsi from 'src/providers/hooks/useNsi';
import {
  color,
  Flex,
  PrimaryText,
  spacing,
  useColorMode,
  useDisclosure,
} from 'deepstash-ui';
import { numberFormatter } from 'utils/global';
import UsersModal from 'src/components/modals/users-modal/UsersModal';
import { Analytics, Events } from 'src/services/analytics';
import useNsiActivity from 'providers/hooks/useNsiActivity';
import { NSI_STASH_ID } from 'providers/activity/ActivityReducerUtils';

const StashOptionsMenu = dynamic(
  () => import('src/components/stash-options-menu/StashOptionsMenu'),
);

interface IdeaStashButtonProps {
  idea: Idea;
  source: Source | null;
  /**
   * Signal up the tree that this idea got stashed
   */
  onIdeaStashed?: () => void;
  /**
   * Signal up the tree that this idea got unstashed
   */
  onIdeaUnstashed?: () => void;
}

const IdeaStashButton: React.FC<IdeaStashButtonProps> = ({
  idea,
  onIdeaStashed,
  onIdeaUnstashed,
  source,
}) => {
  const { isLoggedIn } = useAuth();
  const { colorMode } = useColorMode();
  const { activity, toggleIdeaStash } = useActivity();
  const { id: ideaId, totalSaves } = idea;
  const { stash: nsiStash, getCanUseNsiActivity } = useNsiActivity();

  const {
    isOpen: isUsersModalOpen,
    onOpen: onUsersModalOpen,
    onClose: onUsersModalClose,
  } = useDisclosure();

  const currentSelectedStashId = useMemo(
    () =>
      !isLoggedIn && getCanUseNsiActivity() && nsiStash.stashedIdeas[ideaId]
        ? NSI_STASH_ID
        : activity?.stashedIdeas?.find(x => x[0] === ideaId)?.[1],
    [activity?.stashedIdeas, ideaId, nsiStash.stashedIdeas],
  );

  //If this is an idea the current user publisehd without a stash, it will appear in the stashed idea id, but won't have a stash associated
  const isStashed =
    (!isLoggedIn && !!currentSelectedStashId) ||
    (activity?.stashedIdeasIds?.includes(ideaId) ?? false);

  const { nsiDispatch } = useNsi();

  const onStashClick = (stashId: number) => {
    if (!source) {
      return;
    }
    if ((!isLoggedIn || !activity) && !getCanUseNsiActivity()) {
      nsiDispatch({
        type: 'openAuth',
        payload: {
          type: 'sign-in',
        },
      });
      return;
    }

    Analytics.logEvent({
      eventName: Events.organising.saveIdea,
      properties: {
        ideaId: ideaId,
        sourceId: source.id,
        destination: 'stash',
      },
      platforms: ['amplitude'],
    });

    toggleIdeaStash({
      ideaId: ideaId,
      sourceId: source.id,
      stashId: stashId,
      onSuccess: () => {
        onIdeaStashed?.();
      },
    });
  };

  const onUnstashClick = () => {
    if (!source) {
      return;
    }
    // If the user is not logged in we want to show the auth modal when he tries to stash an idea
    // However, if we are on a page that allows NSI activity tracking, don't show the modal
    if ((!isLoggedIn || !activity) && !getCanUseNsiActivity()) {
      nsiDispatch({
        type: 'openAuth',
        payload: {
          type: 'sign-in',
        },
      });
      return;
    }

    Analytics.logEvent({
      eventName: Events.organising.removeIdea,
      properties: {
        ideaId: ideaId,
      },
      platforms: ['amplitude'],
    });

    toggleIdeaStash({
      ideaId: ideaId,
      sourceId: source.id,
      onSuccess: () => {
        onIdeaUnstashed?.();
      },
    });
  };

  const formattedNumStashes = useMemo(
    () =>
      numberFormatter(
        // Add one save for the NSI stash
        totalSaves + (!isLoggedIn && nsiStash.stashedIdeas[ideaId] ? 1 : 0),
      ),
    [totalSaves, nsiStash.stashedIdeas],
  );

  const onStashLabelClick = (
    ev: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    ev.stopPropagation();
    ev.preventDefault();
    if (ideaId === -1) return;

    Analytics.logEvent({
      eventName: Events.consumption.contentTap,
      properties: {
        id: ideaId,
        type: 'idea',
        element: 'save-count',
        location: 'idea-card',
      },
      platforms: ['amplitude'],
    });

    onUsersModalOpen();
  };

  return (
    <Flex align="center">
      {source && (
        <StashOptionsMenu
          isStashed={isStashed}
          selectedStashId={currentSelectedStashId}
          onStash={onStashClick}
          onUnstash={onUnstashClick}
          _hover={{ backgroundColor: color[colorMode].surface }}
          id={`idea-stash-button-${ideaId}`}
          ideaId={ideaId}
          source={source}
        />
      )}
      <Flex
        px={spacing.XS.rem}
        onClick={onStashLabelClick}
        _hover={{ textDecoration: 'underline' }}
        align="center"
      >
        <PrimaryText size="m" color={color[colorMode].text}>
          {formattedNumStashes}
        </PrimaryText>
      </Flex>
      {isUsersModalOpen && (
        <UsersModal
          onClose={onUsersModalClose}
          isOpen={isUsersModalOpen}
          id={ideaId}
          usecase={UsersModalType.SAVES}
        />
      )}
    </Flex>
  );
};

export default IdeaStashButton;
